import { FC, ReactElement, useState, useEffect } from 'react';
import { Grid, Paper, Typography, List, ListItem, Box, Card } from '@mui/material';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import { useNavigate, useLocation } from 'react-router-dom';
import { BakeasyAppBar } from '../../components/layouts/BakeasyAppBar';
import { t } from 'i18next';
import * as BakeasyButton from '../../components/buttons/BakeasyButtons';
import { GRAY_200 } from '../../constants/Colors';
import {
  sendReply,
  messagesState,
  getReplies,
  clearMessagesState,
  fetchNewMessageCount
} from '../../redux_store/slices/messagesSlice';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import { formatDateTime } from '../../utils/DateUtils';
import { validateField } from '../../utils/ValidationUtils';
import BakeasyMessageToList from '../../components/common/BakeasyMessageToList';

export const MessagesThread: FC = (): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [messages, setMessages] = useState<[]>([]);
  const [sendMessage, setSendMessage] = useState<any>();

  let { selectedValue } = useAppSelector(messagesState);

  const handleSendMessages = async () => {
    if (!validateField(dispatch, 'MESSAGE_IS_REQUIRED', sendMessage)) {
      return false;
    }

    try {
      await dispatch(sendReply({ content: sendMessage, messageId: location?.state?._id }));
      dispatch(fetchNewMessageCount(''));
      fetchReplies();
      setSendMessage('');
    } catch (error) {
      console.error('Error while sending message:', error);
    }
  };

  const fetchReplies = async () => {
    try {
      await dispatch(getReplies({ id: location?.state?._id }));
    } catch (error) {
      console.error('Error while sending message:', error);
    }
  };

  useEffect(() => {
    if (Array.isArray(selectedValue)) {
      setMessages(selectedValue);
    }
  }, [selectedValue]);

  useEffect(() => {
    fetchReplies();
  }, []);

  return (
    <Grid>
      <Grid>
        <Grid item>
          <BakeasyAppBar
            title={t('MESSAGES_THREAD')}
            menus={<BakeasyButton.Print onClick={() => {}} />}
            onBackClick={() => {
              dispatch(clearMessagesState());
              navigate(-1);
            }}
          />
        </Grid>

        {location?.state?.to && (
          <Card sx={{ display: 'flex', justifyContent: 'space-around', mt: 1, p: 1 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs>
                <BakeasyMessageToList data={location?.state?.to} />
              </Grid>
            </Grid>
          </Card>
        )}

        <Paper component={Card} variant="outlined" sx={{ mt: 1 }}>
          <List>
            {messages?.map((message: any, index: any) => (
              <ListItem
                key={index}
                style={{
                  display: 'flex',
                  justifyContent: message.replyStatus === 'Send' ? 'flex-end' : 'flex-start',
                  paddingLeft: message.replyStatus === 'Send' ? '90px' : '16px', // Add margin conditionally
                  paddingRight: message.replyStatus !== 'Send' ? '90px' : '16px',
                  width: '100%' // Add margin conditionally
                }}
              >
                <Box
                  style={{
                    padding: '8px',
                    borderRadius: '8px',
                    backgroundColor: message.replyStatus === 'Send' ? 'white' : GRAY_200,
                    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                    maxWidth: '100%', // Restrict Box width to avoid overflowing
                    wordWrap: 'break-word' // Break long words that might overflow
                  }}
                >
                  <Typography
                    variant="caption"
                    style={{
                      display: 'block',
                      marginBottom: '4px',
                      fontWeight: 600,
                      fontSize: '0.8rem',
                      textAlign: message.replyStatus === 'Send' ? 'right' : 'left'
                    }}
                  >
                    {`${message.userName}@${formatDateTime(message.createdDate, true)}`}
                  </Typography>
                  <Typography variant="body2">{message.content}</Typography>
                </Box>
              </ListItem>
            ))}
          </List>
        </Paper>

        {!location?.state?.disabled && (
          <Card sx={{ display: 'flex', justifyContent: 'space-around', mt: 1, p: 1 }}>
            <Grid container alignItems="center" spacing={2}>
              <Grid item xs>
                <TextField.ReplyMessage
                  fullWidth
                  value={sendMessage}
                  onChange={event => setSendMessage(event.target.value)}
                />
              </Grid>

              <Grid item>
                <BakeasyButton.Send onClick={handleSendMessages} />
              </Grid>
            </Grid>
          </Card>
        )}
      </Grid>
    </Grid>
  );
};
